import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Home, ShoppingCart, Package, LogOut, Calendar, TrendingUp, FileText, Eye, EyeOff, Download, Wallet, Trash2, User, Camera, Key, ExternalLink, Truck, Users, UserCheck, Receipt, Clock, RefreshCw, AlertTriangle, HelpCircle, Printer } from 'lucide-react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import StaffManagement from '../components/StaffManagement';
import StaffManagementV2 from '../components/StaffManagementV2';
import AttendancePunch from '../components/AttendancePunch';
import BottomNavigation from '../components/BottomNavigation';
import FranchiseExpenseTracker from './FranchiseExpenseTracker';
import LogoLoader from '../components/LogoLoader';
import SalesInsights from '../components/SalesInsights';
import FOSWatermark from '../components/FOSWatermark';
import GuidedTour from '../components/GuidedTour';
import StartTourDialog from '../components/StartTourDialog';
import { TOUR_DEFINITIONS, resetTour } from '../services/tourService';
import { DashboardStatsSkeleton, OrderListSkeleton, QuickActionsSkeleton, SalesChartSkeleton } from '../components/SkeletonLoaders';
import LanguageSelector from '../components/LanguageSelector';
import jsPDF from 'jspdf';
import nativeThermalPrinter from '../services/nativeThermalPrinterService';
import { performLogout } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FranchiseDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Subscription expiry state
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  
  // Get initial tab from URL query param or default to 'dashboard'
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    return tabParam || 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const { isOnline } = useOnlineStatus();
  const [todaySales, setTodaySales] = useState(0);
  const [todayOrders, setTodayOrders] = useState(0);
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true); // For skeleton loaders
  const [reportData, setReportData] = useState(null);
  const [reportFilters, setReportFilters] = useState({ start_date: '', end_date: '' });
  const [hideSales, setHideSales] = useState(true); // Hidden by default
  const [isViewAllOrdersOpen, setIsViewAllOrdersOpen] = useState(false);
  const [allTodayOrders, setAllTodayOrders] = useState([]);
  const [isDeleteOrderOpen, setIsDeleteOrderOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletePin, setDeletePin] = useState('');
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isChangePinOpen, setIsChangePinOpen] = useState(false);
  const [pinChangeData, setPinChangeData] = useState({
    currentPin: '',
    newPin: '',
    confirmNewPin: ''
  });
  const [franchisePhoto, setFranchisePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isSalesVisible, setIsSalesVisible] = useState(false);
  const [isVerifyPinOpen, setIsVerifyPinOpen] = useState(false);
  const [verifyPin, setVerifyPin] = useState('');
  const [salesVisibilityTimer, setSalesVisibilityTimer] = useState(null);
  const [isStaffManagementOpen, setIsStaffManagementOpen] = useState(false);
  const [isAttendanceOpen, setIsAttendanceOpen] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const lastFetchDateRef = React.useRef(new Date().toISOString().split('T')[0]);
  
  // Expense Tracker state
  const [showExpenseTracker, setShowExpenseTracker] = useState(false);
  
  // Reports PIN verification
  const [isReportsPinOpen, setIsReportsPinOpen] = useState(false);
  const [reportsPin, setReportsPin] = useState('');
  const [isReportsUnlocked, setIsReportsUnlocked] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [offlineInitialized, setOfflineInitialized] = useState(false);

  // Tour state - Initialize as false, only run on first login
  const [showTourDialog, setShowTourDialog] = useState(false);
  const [forceTourRun, setForceTourRun] = useState(false);
  const [currentTourId, setCurrentTourId] = useState(null);
  const [pendingReportsTour, setPendingReportsTour] = useState(false);
  const [pendingStaffTour, setPendingStaffTour] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(user?.has_completed_tour ?? true); // Default true to prevent auto-run

  // Staff Management PIN verification
  const [staffPinOpen, setStaffPinOpen] = useState(false);
  const [staffPin, setStaffPin] = useState('');
  
  // Reprint order state
  const [isReprintPinOpen, setIsReprintPinOpen] = useState(false);
  const [reprintPin, setReprintPin] = useState('');
  const [orderToReprint, setOrderToReprint] = useState(null);
  const [reprintLoading, setReprintLoading] = useState(false);
  
  // Mark tour as completed on server
  const markTourCompletedOnServer = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (!token) return;
      
      await axios.post(`${API}/franchise/tour-completed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setHasCompletedTour(true);
      
      // Update user object in parent state if setUser is available
      if (setUser && user) {
        setUser({ ...user, has_completed_tour: true });
      }
    } catch (error) {
      console.error('Failed to mark tour as completed:', error);
    }
  };

  // Handle tour completion callback
  const handleTourComplete = () => {
    setForceTourRun(false);
    setCurrentTourId(null);
    
    // Only mark on server if this was the first-time tour
    if (!hasCompletedTour) {
      markTourCompletedOnServer();
    }
  };

  // Handle tour start from dialog
  const handleStartTour = (tourId) => {
    setCurrentTourId(tourId);
    setForceTourRun(true);
    // Navigate to the appropriate tab/page based on tour
    if (tourId === 'dashboard') {
      setActiveTab('dashboard');
    } else if (tourId === 'viewEye') {
      setActiveTab('dashboard');
    } else if (tourId === 'rawMaterialOrder') {
      navigate('/raw-material-order?tour=true');
    } else if (tourId === 'staffManagement') {
      // Staff Management requires PIN - open PIN dialog first
      setStaffPinOpen(true);
      setPendingStaffTour(true);
    } else if (tourId === 'ledgerManagement') {
      navigate('/ledger-management?tour=true');
    } else if (tourId === 'reports') {
      // Reports require PIN - unlock first then show tour
      if (!isReportsUnlocked) {
        setIsReportsPinOpen(true);
        setPendingReportsTour(true);
      } else {
        setActiveTab('reports');
        setForceTourRun(true);
        setCurrentTourId('reports');
      }
    } else if (tourId === 'attendance') {
      setIsAttendanceOpen(true);
    } else if (tourId === 'generateOrder') {
      navigate('/generate-order?tour=true');
    }
  };

  // Get current tour steps based on active tab
  const getCurrentTourSteps = () => {
    if (activeTab === 'dashboard' && !forceTourRun) {
      return TOUR_DEFINITIONS.dashboard?.steps || [];
    }
    if (activeTab === 'reports' && forceTourRun && currentTourId === 'reports') {
      return TOUR_DEFINITIONS.reports?.steps || [];
    }
    if (forceTourRun && currentTourId) {
      return TOUR_DEFINITIONS[currentTourId]?.steps || [];
    }
    return [];
  };

  // Live clock update every second
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    
    return () => clearInterval(clockInterval);
  }, []);

  // Force sync pending orders on page load - now just refreshes data
  useEffect(() => {
    // Refresh data on load
    console.log('[FranchiseDashboard] Refreshing data on load...');
  }, []);

  // First-time tour trigger - only on first login when has_completed_tour is false
  useEffect(() => {
    // Only trigger if user hasn't completed tour and we're on the dashboard tab
    if (user?.has_completed_tour === false && activeTab === 'dashboard' && !forceTourRun) {
      // Small delay to let the dashboard render fully
      const timer = setTimeout(() => {
        setHasCompletedTour(false);
        setCurrentTourId('dashboard');
        setForceTourRun(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []); // Only run on mount, not on route changes

  // Check subscription expiry on mount and periodically
  useEffect(() => {
    const checkSubscriptionExpiry = async () => {
      try {
        const franchiseId = user?.franchise?.id;
        if (!franchiseId) return;
        
        const response = await axios.get(`${API}/franchise-subscription/check-expiry/${franchiseId}`);
        
        if (response.data.is_expired) {
          setIsSubscriptionExpired(true);
          // Auto logout after showing message
          setTimeout(() => {
            performLogout(setUser, null, '/');
          }, 5000);
        }
      } catch (error) {
        console.error('Error checking subscription expiry:', error);
      }
    };

    checkSubscriptionExpiry();
    
    // Check every 5 minutes
    const interval = setInterval(checkSubscriptionExpiry, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [user?.franchise?.id]);

  // Handle URL query param changes (e.g., when coming from Ledger with ?tab=reports)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
      // Clear the query param after reading to keep URL clean
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  // Initialize and load data
  useEffect(() => {
    const initAndLoadData = async () => {
      setInitialLoading(true);
      setDataLoading(true);
      
      try {
        console.log('[Dashboard] Loading initial data...');
        await Promise.all([
          fetchTodayData(),
          fetchOrders()
        ]);
        
        // Load non-critical data in background
        fetchInventory();
        
        setOfflineInitialized(true);
        console.log('[Dashboard] Data loaded successfully');
        
      } catch (error) {
        console.error('[Dashboard] Initialization error:', error);
      } finally {
        setInitialLoading(false);
        setDataLoading(false);
      }
    };
    
    initAndLoadData();
  }, [user.franchise.id, user.client_id]);

  // Refresh data when app becomes visible (e.g., after overnight, switching tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentDate = new Date().toISOString().split('T')[0];
        // If the date has changed since last fetch, refresh data
        if (currentDate !== lastFetchDateRef.current) {
          console.log('[Dashboard] Date changed, refreshing data...');
          lastFetchDateRef.current = currentDate;
          fetchTodayData();
          fetchOrders();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check on window focus (for PWA)
    const handleFocus = () => {
      const currentDate = new Date().toISOString().split('T')[0];
      if (currentDate !== lastFetchDateRef.current) {
        console.log('[Dashboard] Date changed on focus, refreshing data...');
        lastFetchDateRef.current = currentDate;
        fetchTodayData();
        fetchOrders();
      }
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const updateSyncStatus = async () => {
    // Sync status no longer needed without offline manager
    setPendingSyncCount(0);
  };

  const fetchTodayData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      lastFetchDateRef.current = today;
      
      const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
      
      const [salesResponse, ordersResponse] = await Promise.all([
        axios.post(`${API}/reports/sales`, {
          franchise_id: user.franchise.id,
          start_date: today,
          end_date: today
        }, { headers }),
        axios.get(`${API}/orders?franchise_id=${user.franchise.id}&start_date=${today}&end_date=${today}`, { headers })
      ]);
      
      // Set sales data
      setTodaySales(salesResponse.data.grand_total || 0);
      
      // Set orders
      const serverOrders = ordersResponse.data.orders || [];
      const activeOrders = serverOrders.filter(order => !order.is_deleted);
      setTodayOrders(activeOrders.length);
      setOrders(activeOrders.slice(0, 20));
      
    } catch (error) {
      console.error('[Dashboard] Error fetching today data:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders?franchise_id=${user.franchise.id}&limit=20`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
      });
      const serverOrders = response.data.orders || [];
      const activeOrders = serverOrders.filter(order => !order.is_deleted);
      
      const finalOrders = activeOrders
        .sort((a, b) => new Date(b.date_timestamp) - new Date(a.date_timestamp))
        .slice(0, 20);
      
      setOrders(finalOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchInventory = async () => {
    // Inventory is not critical for initial dashboard - load it lazily
    try {
      const response = await axios.get(`${API}/inventory`);
      setInventory(response.data.inventory || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const handleGenerateReport = async () => {
    try {
      const response = await axios.post(`${API}/reports/sales`, {
        franchise_id: user.franchise.id,
        start_date: reportFilters.start_date,
        end_date: reportFilters.end_date
      });
      setReportData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };

  const handleViewAllOrders = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(
        `${API}/orders?franchise_id=${user.franchise.id}&start_date=${today}&end_date=${today}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
      );
      const serverOrders = response.data.orders || [];
      
      const sortedOrders = serverOrders
        .sort((a, b) => new Date(b.date_timestamp) - new Date(a.date_timestamp));
      
      setAllTodayOrders(sortedOrders);
      setIsViewAllOrdersOpen(true);
    } catch (error) {
      console.error('Error fetching all orders:', error);
      toast.error('Failed to load orders');
    }
  };

  const handleDownloadReportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('1946 - Filter Coffee & Tea', pageWidth / 2, 20, { align: 'center' });
    
    // Report Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const now = new Date();
    doc.text(`Date: ${now.toLocaleDateString()}`, 14, 35);
    doc.text(`Time: ${now.toLocaleTimeString()}`, 14, 40);
    doc.text(`Report Period: ${reportFilters.start_date || 'All'} to ${reportFilters.end_date || 'All'}`, 14, 45);
    
    // Franchise Details
    doc.setFont(undefined, 'bold');
    doc.text('Franchise Details:', 14, 55);
    doc.setFont(undefined, 'normal');
    doc.text(`Name: ${user.franchise.name}`, 14, 60);
    doc.text(`Location: ${user.franchise.location}`, 14, 65);
    doc.text(`Contact: ${user.franchise.contact_number}`, 14, 70);
    
    // Line separator
    doc.line(14, 75, pageWidth - 14, 75);
    
    // Sales Report Title
    doc.setFont(undefined, 'bold');
    doc.setFontSize(14);
    doc.text('Sales Report', 14, 85);
    
    // Table Headers
    doc.setFontSize(10);
    let yPosition = 95;
    doc.text('Item Name', 14, yPosition);
    doc.text('Price/Unit', 80, yPosition);
    doc.text('Quantity', 120, yPosition);
    doc.text('Total', 160, yPosition, { align: 'right' });
    
    // Line under headers
    doc.line(14, yPosition + 2, pageWidth - 14, yPosition + 2);
    yPosition += 10;
    
    // Table Data with Categories
    doc.setFont(undefined, 'normal');
    if (reportData && reportData.categories) {
      reportData.categories.forEach((category) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Category header
        doc.setFont(undefined, 'bold');
        doc.setFontSize(11);
        doc.text(category.category.toUpperCase(), 14, yPosition);
        yPosition += 8;
        
        // Category items
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        category.items.forEach((item) => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text('  ' + item.name, 14, yPosition);
          doc.text(`Rs ${item.price}`, 80, yPosition);
          doc.text(`${item.quantity}`, 120, yPosition);
          doc.text(`Rs ${item.total.toFixed(2)}`, 160, yPosition, { align: 'right' });
          yPosition += 7;
        });
        
        // Category subtotal
        doc.setFont(undefined, 'bold');
        doc.text(`Subtotal (${category.category}):`, 14, yPosition);
        doc.text(`Rs ${category.subtotal.toFixed(2)}`, 160, yPosition, { align: 'right' });
        yPosition += 10;
      });
    }
    
    // Line above grand total
    doc.line(14, yPosition, pageWidth - 14, yPosition);
    yPosition += 8;
    
    // Grand Total
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Grand Total:', 14, yPosition);
    doc.text(`Rs ${reportData?.grand_total?.toFixed(2) || '0.00'}`, 160, yPosition, { align: 'right' });
    
    // Footer
    yPosition = doc.internal.pageSize.getHeight() - 30;
    doc.setFontSize(10);
    doc.setFont(undefined, 'italic');
    doc.text('Thank you for your business!', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 6;
    doc.setFontSize(8);
    doc.text('* This receipt is for your reference only', pageWidth / 2, yPosition, { align: 'center' });
    
    // Save PDF
    const fileName = `Sales_Report_${user.franchise.name.replace(/\s+/g, '_')}_${now.toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('Report downloaded successfully!');
  };

  const handleLogout = () => {
    // Use performLogout which clears all user data and does a full page reload
    // This prevents data leakage between different user accounts
    performLogout(setUser, null, '/');
  };

  const handleViewOrder = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const handleDeleteClick = (e, order) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    setOrderToDelete(order);
    setIsDeleteOrderOpen(true);
  };

  const handleDeleteOrder = async () => {
    if (!deleteReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    if (!deletePin) {
      toast.error('Please enter your PIN');
      return;
    }

    try {
      await axios.delete(`${API}/orders/${orderToDelete.order_no}`, {
        data: {
          reason: deleteReason,
          pin: deletePin,
          franchise_id: user.franchise.id
        }
      });

      toast.success('Order deleted successfully');
      setIsDeleteOrderOpen(false);
      setOrderToDelete(null);
      setDeleteReason('');
      setDeletePin('');
      
      // Refresh data
      fetchTodayData();
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      setFranchisePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!franchisePhoto) return;

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', franchisePhoto);
      formData.append('franchise_id', user.franchise.id);

      const response = await axios.post(`${API}/franchises/${user.franchise.id}/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Photo uploaded successfully!');
      
      // Build full photo URL - handle both relative and absolute URLs
      let photoUrl = response.data.photo_url;
      if (photoUrl && !photoUrl.startsWith('http')) {
        // Relative URL - prepend backend URL
        photoUrl = `${BACKEND_URL}${photoUrl}`;
      }
      
      // Update user object with new photo URL
      const updatedUser = {
        ...user,
        franchise: {
          ...user.franchise,
          photo_url: photoUrl
        }
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setFranchisePhoto(null);
      setPhotoPreview(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to upload photo: ${errorMsg}`);
      console.error('Photo upload error:', error);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();

    // Validation
    if (pinChangeData.newPin !== pinChangeData.confirmNewPin) {
      toast.error('New PIN and confirmation do not match');
      return;
    }

    if (pinChangeData.newPin.length !== 5) {
      toast.error('PIN must be exactly 5 digits');
      return;
    }

    if (!/^\d+$/.test(pinChangeData.newPin)) {
      toast.error('PIN must contain only numbers');
      return;
    }

    try {
      await axios.post(`${API}/franchises/${user.franchise.id}/change-pin`, {
        current_pin: pinChangeData.currentPin,
        new_pin: pinChangeData.newPin
      });

      toast.success('PIN changed successfully! Please use the new PIN for your next login.');
      setIsChangePinOpen(false);
      setPinChangeData({ currentPin: '', newPin: '', confirmNewPin: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change PIN');
    }
  };

  const handleViewSalesClick = () => {
    setIsVerifyPinOpen(true);
  };

  const handleVerifyPin = async (e) => {
    e.preventDefault();

    if (!verifyPin) {
      toast.error('Please enter your PIN');
      return;
    }

    // Verify PIN matches franchise PIN
    try {
      const response = await axios.post(`${API}/franchises/verify-pin`, {
        franchise_id: user.franchise.id,
        pin: verifyPin
      });

      if (response.data.success) {
        setIsSalesVisible(true);
        setIsVerifyPinOpen(false);
        setVerifyPin('');
        toast.success('Sales data unlocked for 10 seconds');

        // Clear any existing timer
        if (salesVisibilityTimer) {
          clearTimeout(salesVisibilityTimer);
        }

        // Auto-hide after 10 seconds
        const timer = setTimeout(() => {
          setIsSalesVisible(false);
          toast.info('Sales data hidden');
        }, 10000);

        setSalesVisibilityTimer(timer);
      } else {
        toast.error('Incorrect PIN');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid PIN');
      setVerifyPin('');
    }
  };

  const handleHideSales = () => {
    if (salesVisibilityTimer) {
      clearTimeout(salesVisibilityTimer);
    }
    setIsSalesVisible(false);
    toast.info('Sales data hidden');
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (salesVisibilityTimer) {
        clearTimeout(salesVisibilityTimer);
      }
    };
  }, [salesVisibilityTimer]);

  // Handle reprint click - opens PIN verification
  const handleReprintClick = (e, order) => {
    e.stopPropagation();
    setOrderToReprint(order);
    setReprintPin('');
    setIsReprintPinOpen(true);
  };

  // Verify PIN and reprint receipt
  const handleReprintVerify = async (e) => {
    e.preventDefault();
    
    if (!reprintPin) {
      toast.error('Please enter your PIN');
      return;
    }

    try {
      // Verify PIN first
      const response = await axios.post(`${API}/franchises/verify-pin`, {
        franchise_id: user.franchise.id,
        pin: reprintPin
      });

      if (response.data.success) {
        setIsReprintPinOpen(false);
        setReprintPin('');
        
        // Now print the receipt
        await handlePrintReceipt(orderToReprint);
        setOrderToReprint(null);
      } else {
        toast.error('Incorrect PIN');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid PIN');
      setReprintPin('');
    }
  };

  // Print receipt for an order
  const handlePrintReceipt = async (order) => {
    setReprintLoading(true);
    try {
      // Check if printer is connected
      const status = nativeThermalPrinter.getStatus();
      
      if (!status.connected) {
        toast.info('No printer connected. Connect a USB thermal printer in Settings.');
        return;
      }
      
      // Build order data for receipt
      const receiptOrderData = {
        order_no: order.order_no,
        date_timestamp: order.date_timestamp,
        order_type: order.order_type || null,
        table_no: order.table_no || null,
        token_no: order.token_no || null,
        customer_name: order.customer_name || null,
        customer_mobile: order.customer_mobile || null,
        items: order.items?.map(item => ({
          name: item.item_name_snapshot || item.name,
          quantity: item.quantity,
          price: parseFloat(item.item_price_snapshot || item.price),
          line_total: item.quantity * parseFloat(item.item_price_snapshot || item.price)
        })) || [],
        total_value: order.total_value || order.total_amount || order.grand_total || 0,
        payment_method: order.payment_method || 'cash',
        ledger_name: order.ledger_name || null,
        ledger: order.ledger || 'cash'
      };
      
      // Get franchise data with brand_name from user context
      const franchiseData = {
        brand_name: user?.franchise?.brand_name || user?.franchise?.name || '1946 Filter Coffee & Tea',
        franchise_name: user?.franchise?.franchise_name || user?.franchise?.name || '',
        franchise_address: user?.franchise?.franchise_address || user?.franchise?.address || '',
        contact_number: user?.franchise?.contact_number || user?.franchise?.mobile_number || '',
        pincode: user?.franchise?.pincode || '',
        gstin: user?.franchise?.gstin || ''
      };
      
      // Print using native thermal printer service
      const result = await nativeThermalPrinter.print(receiptOrderData, franchiseData);
      
      if (result.success) {
        toast.success('Receipt reprinted successfully!');
      } else {
        toast.error(result.error || 'Failed to print receipt');
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
      toast.error('Failed to print receipt: ' + error.message);
    } finally {
      setReprintLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Initial Loading Screen */}
      {initialLoading && (
        <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
          <LogoLoader size="lg" text="Loading Dashboard..." />
        </div>
      )}
      
      {/* Subscription Expired Overlay */}
      {isSubscriptionExpired && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              Subscription Expired
            </h2>
            <p className="text-gray-600 mb-6">
              Your franchise subscription has expired. Please contact your administrator to renew the subscription and continue accessing the portal.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Contact Admin:</strong> Ask your business administrator to renew your outlet's subscription from the Admin Portal.
              </p>
            </div>
            <p className="text-sm text-gray-400">
              You will be automatically logged out in a few seconds...
            </p>
          </div>
        </div>
      )}
      
      {/* Top spacing - NetworkStatus bar temporarily disabled */}
      <div className="h-2"></div>
      <div className="border-b border-border bg-surface shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 sm:gap-4">
            <img src="/fos-logo.png" alt="FOS Logo" className="h-10 sm:h-12 w-auto object-contain" />
            <div>
              <h1 className="text-xl font-bold text-primary">Franchise Portal</h1>
              <p className="text-sm text-secondary">{user.franchise.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Help/Tour Button */}
            <Button
              data-testid="start-tour-btn"
              variant="outline"
              className="h-10 w-10 p-0 rounded-full"
              onClick={() => setShowTourDialog(true)}
              title="Guided Tours"
            >
              <HelpCircle className="h-5 w-5 text-amber-600" />
            </Button>
            <LanguageSelector variant="dropdown" />
            <Button
              variant="outline"
              className="h-10 rounded-full px-4 font-bold"
              onClick={() => setIsProfileOpen(true)}
            >
              <User className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Profile</span>
            </Button>
            <Button
              data-testid="franchise-logout-button"
              variant="outline"
              className="h-10 rounded-full px-4 sm:px-6 font-bold"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 pb-20">
        {activeTab === 'dashboard' && (
          <div className="space-y-6" data-testid="dashboard-welcome">
            {/* Dashboard Tour - Only runs on first login */}
            <GuidedTour
              tourId="dashboard"
              steps={TOUR_DEFINITIONS.dashboard?.steps || []}
              forceRun={forceTourRun && currentTourId === 'dashboard'}
              onComplete={handleTourComplete}
            />
            
            {/* Show skeleton while data loads */}
            {dataLoading ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DashboardStatsSkeleton />
                  <QuickActionsSkeleton />
                </div>
                <SalesChartSkeleton />
                <OrderListSkeleton count={5} />
              </>
            ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="dashboard-stats">
              <Card data-testid="today-sales-card" className="shadow-sm border-border">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold text-primary flex items-center">
                      <TrendingUp className="mr-2 h-5 w-5 text-accent" />
                      Today's Sales
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={isSalesVisible ? handleHideSales : handleViewSalesClick}
                      className="h-8 w-8 p-0 rounded-full hover:bg-accent/10"
                      title={isSalesVisible ? "Hide sales (auto-hides in 10s)" : "View sales (requires PIN)"}
                    >
                      {isSalesVisible ? <EyeOff className="h-4 w-4 text-accent" /> : <Eye className="h-4 w-4 text-accent" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isSalesVisible ? (
                    <div>
                      <p className="text-4xl font-bold text-accent">₹{todaySales.toFixed(2)}</p>
                      <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                        <span className="w-2 h-2 bg-orange-600 rounded-full animate-pulse"></span>
                        Auto-hides in 10s
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-4xl font-bold text-secondary">₹ •••••</p>
                      <p className="text-xs text-secondary mt-1">Click eye to view</p>
                    </div>
                  )}
                  <p className="text-sm text-secondary mt-2">{todayOrders} orders</p>
                </CardContent>
              </Card>

              <Card className="shadow-sm border-border" data-testid="quick-actions">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-bold text-primary flex items-center">
                      <FileText className="mr-2 h-5 w-5 text-accent" />
                      Quick Actions
                    </CardTitle>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-primary">
                        {currentDateTime.toLocaleDateString('en-IN', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })}
                      </p>
                      <p className="text-xs text-secondary">
                        {currentDateTime.toLocaleDateString('en-IN', { weekday: 'long' })}
                      </p>
                      <p className="text-sm font-mono font-semibold text-accent">
                        {currentDateTime.toLocaleTimeString('en-IN', { 
                          hour: '2-digit', 
                          minute: '2-digit', 
                          second: '2-digit',
                          hour12: true 
                        }).toUpperCase()}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    data-testid="new-order-button"
                    onClick={() => navigate('/generate-order')}
                    className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider"
                  >
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    Generate Order
                  </Button>
                  <Button
                    data-testid="expense-tracker-button"
                    variant="outline"
                    className="w-full h-12 rounded-full font-bold uppercase tracking-wider border-amber-500 text-amber-700 hover:bg-amber-50"
                    onClick={() => setShowExpenseTracker(true)}
                  >
                    <Receipt className="mr-2 h-5 w-5" />
                    Expense Tracker
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Sales Insights Chart */}
            <SalesInsights 
              franchiseId={user?.franchise?.id}
              franchisePin={user?.franchise?.pin_code}
            />

            <Card className="shadow-sm border-border" data-testid="recent-orders">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-xl font-bold text-primary">Recent Orders</CardTitle>
                {orders.length > 10 && (
                  <Button
                    onClick={handleViewAllOrders}
                    variant="outline"
                    size="sm"
                    className="h-9 rounded-full font-bold"
                  >
                    View All
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orders
                    .sort((a, b) => new Date(b.date_timestamp) - new Date(a.date_timestamp))
                    .slice(0, 10)
                    .map((order, index) => (
                      <div
                        key={order.order_no}
                        data-testid={index === 0 ? 'recent-order-first' : `order-item-${order.order_no}`}
                        className={`flex justify-between items-center p-4 rounded-xl hover:bg-muted/70 cursor-pointer transition-colors ${
                          order.is_deleted ? 'bg-red-50 border-2 border-red-200' : 
                          order.sync_status === 'pending' ? 'bg-orange-50 border-2 border-orange-200' : 'bg-muted'
                        }`}
                        onClick={() => handleViewOrder(order.order_no)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-bold ${order.is_deleted ? 'text-red-600 line-through' : 'text-primary'}`}>
                              {order.order_no}
                            </p>
                            {order.is_deleted && (
                              <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">
                                DELETED
                              </span>
                            )}
                            {/* FOS Connect customer tag */}
                            {order.customer_tag_status && order.customer_tag_status !== 'guest' && (
                              <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${
                                order.customer_tag_status === 'existing' 
                                  ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                                  : 'bg-green-50 text-green-600 border border-green-200'
                              }`} data-testid={`customer-tag-${order.order_no}`}>
                                {order.customer_tag_status === 'existing' ? 'Returning' : 'New'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-secondary">{new Date(order.date_timestamp).toLocaleString()}</p>
                          {/* Customer info line */}
                          {(order.customer_name_snapshot || order.customer_mobile_snapshot) && (
                            <p className="text-xs text-muted-foreground mt-0.5" data-testid={`customer-info-${order.order_no}`}>
                              {order.customer_name_snapshot || ''}{order.customer_name_snapshot && order.customer_mobile_snapshot ? ' · ' : ''}{order.customer_mobile_snapshot || ''}
                            </p>
                          )}
                          {order.is_deleted && (
                            <p className="text-xs text-red-700 mt-1">
                              Deleted: {new Date(order.deleted_at).toLocaleString()} - {order.deleted_reason}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <p className={`text-xl font-bold ${order.is_deleted ? 'text-red-600 line-through' : 'text-accent'}`}>
                            ₹{order.total_value || order.total_amount || order.grand_total || 0}
                          </p>
                          {!order.is_deleted && (
                            <>
                              <button
                                onClick={(e) => handleReprintClick(e, order)}
                                className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                                title="Reprint Receipt"
                                data-testid={`reprint-btn-${order.order_no}`}
                              >
                                <Printer className="h-5 w-5 text-blue-600" />
                              </button>
                              <button
                                onClick={(e) => handleDeleteClick(e, order)}
                                className="p-2 hover:bg-red-50 rounded-full transition-colors"
                                title="Delete Order"
                              >
                                <Trash2 className="h-5 w-5 text-red-600" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  {orders.length === 0 && (
                    <p className="text-center text-secondary py-8">No orders yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
            </>
            )}
          </div>
        )}

        {activeTab === 'reports' && (
          <div>
            {/* Reports Tour */}
            {activeTab === 'reports' && (
              <GuidedTour
                tourId="reports"
                steps={TOUR_DEFINITIONS.reports?.steps || []}
                forceRun={forceTourRun && currentTourId === 'reports'}
                onComplete={handleTourComplete}
              />
            )}
            <h2 className="text-3xl font-bold text-primary mb-6">Sales Reports</h2>
            <Card className="shadow-sm border-border">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-primary">Generate Report</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Quick Date Filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      setReportFilters({ start_date: today, end_date: today });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      const dateStr = yesterday.toISOString().split('T')[0];
                      setReportFilters({ start_date: dateStr, end_date: dateStr });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Yesterday
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date();
                      const last7Days = new Date();
                      last7Days.setDate(today.getDate() - 7);
                      setReportFilters({
                        start_date: last7Days.toISOString().split('T')[0],
                        end_date: today.toISOString().split('T')[0]
                      });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const today = new Date();
                      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                      setReportFilters({
                        start_date: firstDay.toISOString().split('T')[0],
                        end_date: today.toISOString().split('T')[0]
                      });
                    }}
                    className="h-10 rounded-full font-bold"
                  >
                    This Month
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setReportFilters({ start_date: '', end_date: '' })}
                    className="h-10 rounded-full font-bold"
                  >
                    Clear
                  </Button>
                </div>

                {/* Custom Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="franchise-report-start">Start Date</Label>
                    <Input
                      id="franchise-report-start"
                      data-testid="franchise-report-start-input"
                      type="date"
                      value={reportFilters.start_date}
                      onChange={(e) => setReportFilters({ ...reportFilters, start_date: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="franchise-report-end">End Date</Label>
                    <Input
                      id="franchise-report-end"
                      data-testid="franchise-report-end-input"
                      type="date"
                      value={reportFilters.end_date}
                      onChange={(e) => setReportFilters({ ...reportFilters, end_date: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
                <Button data-testid="franchise-generate-report-button" onClick={handleGenerateReport} className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full font-bold uppercase tracking-wider">
                  Generate Report
                </Button>

                {reportData && (
                  <div className="mt-6 p-6 bg-muted rounded-xl">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-primary">Sales Report</h3>
                      <Button
                        onClick={handleDownloadReportPDF}
                        className="h-10 bg-accent text-white hover:bg-accent/90 rounded-full px-4 font-bold flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Download PDF
                      </Button>
                    </div>
                    
                    {reportData.categories && reportData.categories.length > 0 ? (
                      <div className="space-y-6">
                        {reportData.categories.map((category, catIndex) => (
                          <div key={catIndex} className="space-y-3">
                            <h4 className="text-lg font-bold text-primary uppercase tracking-wide border-b-2 border-accent pb-2">
                              {category.category}
                            </h4>
                            {category.items.map((item, itemIndex) => (
                              <div key={itemIndex} className="flex justify-between items-center p-3 bg-white rounded-lg ml-4">
                                <div className="flex-1">
                                  <p className="font-bold text-primary">{item.name}</p>
                                  <p className="text-sm text-secondary">₹{item.price}/unit × {item.quantity} units</p>
                                </div>
                                <p className="text-xl font-bold text-accent">₹{item.total.toFixed(2)}</p>
                              </div>
                            ))}
                            <div className="flex justify-between items-center p-3 bg-accent/10 rounded-lg ml-4 border-l-4 border-accent">
                              <p className="font-bold text-primary">Subtotal ({category.category})</p>
                              <p className="text-lg font-bold text-accent">₹{category.subtotal.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-secondary py-4">No sales data for selected period</p>
                    )}
                    
                    <div className="border-t-2 border-primary pt-4 mt-6">
                      <div className="flex justify-between items-center">
                        <p className="text-xl font-bold text-primary">Grand Total:</p>
                        <p className="text-3xl font-bold text-accent" data-testid="franchise-report-grand-total">
                          ₹{reportData.grand_total ? reportData.grand_total.toFixed(2) : '0.00'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

      </div>

      {/* View All Orders Modal */}
      <Dialog open={isViewAllOrdersOpen} onOpenChange={setIsViewAllOrdersOpen}>
        <DialogContent className="bg-surface max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary">All Today's Orders</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {allTodayOrders
              .sort((a, b) => new Date(b.date_timestamp) - new Date(a.date_timestamp))
              .map((order) => (
                <div
                  key={order.order_no}
                  className={`flex justify-between items-center p-4 rounded-xl hover:bg-muted/70 cursor-pointer transition-colors ${
                    order.is_deleted ? 'bg-red-50 border-2 border-red-200' : 
                    order.sync_status === 'pending' ? 'bg-orange-50 border-2 border-orange-200' : 'bg-muted'
                  }`}
                  onClick={() => {
                    setIsViewAllOrdersOpen(false);
                    handleViewOrder(order.order_no);
                  }}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-bold ${order.is_deleted ? 'text-red-600 line-through' : 'text-primary'}`}>
                        {order.order_no}
                      </p>
                      {order.is_deleted && (
                        <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">
                          DELETED
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-secondary">{new Date(order.date_timestamp).toLocaleString()}</p>
                    {order.is_deleted && (
                      <p className="text-xs text-red-700 mt-1">
                        Deleted: {new Date(order.deleted_at).toLocaleString()} - {order.deleted_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <p className={`text-xl font-bold ${order.is_deleted ? 'text-red-600 line-through' : 'text-accent'}`}>
                      ₹{order.total_value || order.total_amount || order.grand_total || 0}
                    </p>
                    {!order.is_deleted && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsViewAllOrdersOpen(false);
                            handleReprintClick(e, order);
                          }}
                          className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                          title="Reprint Receipt"
                        >
                          <Printer className="h-5 w-5 text-blue-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsViewAllOrdersOpen(false);
                            handleDeleteClick(e, order);
                          }}
                          className="p-2 hover:bg-red-50 rounded-full transition-colors"
                          title="Delete Order"
                        >
                          <Trash2 className="h-5 w-5 text-red-600" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            {allTodayOrders.length === 0 && (
              <p className="text-center text-secondary py-8">No orders today</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNavigation
        isOnline={isOnline}
        activeTab={activeTab}
        showReports={activeTab === 'reports'}
        features={user?.features_enabled || {}}
        onAttendanceClick={() => setIsAttendanceOpen(true)}
        onReportsClick={() => {
          if (isReportsUnlocked) {
            setActiveTab('reports');
          } else {
            setIsReportsPinOpen(true);
          }
        }}
      />

      {/* Delete Order Modal */}
      <Dialog open={isDeleteOrderOpen} onOpenChange={setIsDeleteOrderOpen}>
        <DialogContent className="bg-surface max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-600">Delete Order</DialogTitle>
          </DialogHeader>
          {orderToDelete && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">
                  ⚠️ You are about to delete order: <span className="font-bold">{orderToDelete.order_no}</span>
                </p>
                <p className="text-sm text-red-700 mt-2">
                  Amount: ₹{orderToDelete.total_value}
                </p>
                {orderToDelete.ledger !== 'cash' && (
                  <p className="text-sm text-red-700">
                    This order is linked to a ledger. The balance will be restored.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="delete-reason">Reason for Deletion *</Label>
                <textarea
                  id="delete-reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Enter reason (e.g., Wrong order, Customer cancelled)"
                  className="w-full min-h-[80px] p-3 border rounded-lg mt-1 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>

              <div>
                <Label htmlFor="delete-pin">Confirm with Your PIN *</Label>
                <Input
                  id="delete-pin"
                  type="password"
                  value={deletePin}
                  onChange={(e) => setDeletePin(e.target.value)}
                  placeholder="Enter your 5-digit PIN"
                  maxLength={5}
                  pattern="[0-9]{5}"
                  className="h-12 mt-1"
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDeleteOrderOpen(false);
                    setOrderToDelete(null);
                    setDeleteReason('');
                    setDeletePin('');
                  }}
                  className="flex-1 h-12"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleDeleteOrder}
                  className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete Order
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Profile Modal */}
      <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <DialogContent className="bg-surface max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary">Franchise Profile</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Franchise Photo Section */}
            <div className="flex flex-col items-center gap-4 p-6 bg-muted rounded-xl">
              <div className="relative">
                <div className="h-32 w-32 rounded-full bg-accent/10 flex items-center justify-center overflow-hidden border-4 border-accent/20">
                  {photoPreview || user.franchise.photo_url ? (
                    <img 
                      src={photoPreview || (user.franchise.photo_url?.startsWith('http') ? user.franchise.photo_url : `${BACKEND_URL}${user.franchise.photo_url}`)} 
                      alt="Franchise" 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <User className="h-16 w-16 text-accent/40" />
                  )}
                </div>
                <label 
                  htmlFor="photo-upload" 
                  className="absolute bottom-0 right-0 h-10 w-10 bg-accent text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-accent/90 transition-colors shadow-lg"
                >
                  <Camera className="h-5 w-5" />
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoSelect}
                  />
                </label>
              </div>
              {franchisePhoto && (
                <Button
                  onClick={handlePhotoUpload}
                  disabled={uploadingPhoto}
                  className="h-10 bg-accent text-white hover:bg-accent/90 rounded-full px-6"
                >
                  {uploadingPhoto ? 'Uploading...' : 'Save Photo'}
                </Button>
              )}
              <p className="text-xs text-secondary text-center">Click the camera icon to upload franchise photo</p>
            </div>

            {/* Franchise Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-primary border-b pb-2">Franchise Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-secondary">Franchise Name</Label>
                  <p className="font-semibold text-primary mt-1">{user.franchise.name}</p>
                </div>
                
                <div>
                  <Label className="text-secondary">Franchise ID</Label>
                  <p className="font-semibold text-primary mt-1 text-sm break-all">{user.franchise.id}</p>
                </div>
                
                <div>
                  <Label className="text-secondary">Location</Label>
                  <p className="font-semibold text-primary mt-1">{user.franchise.location || 'N/A'}</p>
                </div>
                
                <div>
                  <Label className="text-secondary">Contact Person</Label>
                  <p className="font-semibold text-primary mt-1">{user.franchise.contact_person || 'N/A'}</p>
                </div>
                
                <div>
                  <Label className="text-secondary">Contact Number</Label>
                  <p className="font-semibold text-primary mt-1">{user.franchise.contact_number || 'N/A'}</p>
                </div>
                
                <div>
                  <Label className="text-secondary">Franchise Model</Label>
                  <p className="font-semibold text-primary mt-1">{user.franchise.franchise_model || 'N/A'}</p>
                </div>
                
                {user.franchise.franchise_address && (
                  <div className="md:col-span-2">
                    <Label className="text-secondary">Address</Label>
                    <p className="font-semibold text-primary mt-1">{user.franchise.franchise_address}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Language Settings */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <LanguageSelector variant="full" />
            </div>

            {/* Change PIN Section */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-primary flex items-center gap-2">
                    <Key className="h-5 w-5 text-accent" />
                    Security Settings
                  </h3>
                  <p className="text-sm text-secondary mt-1">Change your login PIN</p>
                </div>
                <Button
                  onClick={() => {
                    setIsProfileOpen(false);
                    setIsChangePinOpen(true);
                  }}
                  className="h-10 bg-accent text-white hover:bg-accent/90 rounded-full px-4"
                >
                  Change PIN
                </Button>
              </div>
            </div>

            {/* Staff Management Section */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-primary flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-600" />
                    Staff Management
                  </h3>
                  <p className="text-sm text-secondary mt-1">Register and manage staff attendance</p>
                </div>
                <Button
                  onClick={() => {
                    setIsProfileOpen(false);
                    setStaffPinOpen(true);
                  }}
                  className="h-10 bg-purple-600 text-white hover:bg-purple-700 rounded-full px-4"
                >
                  <Key className="h-4 w-4 mr-2" />
                  Manage Staff
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Verify PIN to View Sales Modal */}
      <Dialog open={isVerifyPinOpen} onOpenChange={setIsVerifyPinOpen}>
        <DialogContent className="bg-surface max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <Eye className="h-5 w-5 text-accent" />
              Verify PIN to View Sales
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleVerifyPin} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="verify-pin">Enter your 5-digit PIN</Label>
              <Input
                id="verify-pin"
                type="password"
                value={verifyPin}
                onChange={(e) => setVerifyPin(e.target.value)}
                placeholder="••••••"
                maxLength={5}
                pattern="[0-9]{5}"
                className="h-12 mt-2 text-center text-2xl tracking-widest"
                autoFocus
                required
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                ℹ️ Sales data will be visible for 10 seconds after verification, then auto-hide for security.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsVerifyPinOpen(false);
                  setVerifyPin('');
                }}
                className="flex-1 h-11"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-11 bg-accent text-white hover:bg-accent/90"
              >
                Verify & View
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reprint PIN Verification Modal */}
      <Dialog open={isReprintPinOpen} onOpenChange={(open) => {
        setIsReprintPinOpen(open);
        if (!open) {
          setReprintPin('');
          setOrderToReprint(null);
        }
      }}>
        <DialogContent className="bg-surface max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <Printer className="h-5 w-5 text-blue-600" />
              Verify PIN to Reprint
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleReprintVerify} className="space-y-4 mt-4">
            {orderToReprint && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Order:</strong> {orderToReprint.order_no}
                </p>
                <p className="text-sm text-blue-800">
                  <strong>Amount:</strong> ₹{orderToReprint.total_value || orderToReprint.total_amount || 0}
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="reprint-pin">Enter your 5-digit PIN</Label>
              <Input
                id="reprint-pin"
                type="password"
                value={reprintPin}
                onChange={(e) => setReprintPin(e.target.value)}
                placeholder="••••••"
                maxLength={5}
                pattern="[0-9]{5}"
                className="h-12 mt-2 text-center text-2xl tracking-widest"
                autoFocus
                required
                data-testid="reprint-pin-input"
              />
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                ⚠️ PIN verification is required to reprint receipts for security purposes.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsReprintPinOpen(false);
                  setReprintPin('');
                  setOrderToReprint(null);
                }}
                className="flex-1 h-11"
                disabled={reprintLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-11 bg-blue-600 text-white hover:bg-blue-700"
                disabled={reprintLoading}
                data-testid="reprint-verify-btn"
              >
                {reprintLoading ? 'Printing...' : 'Verify & Print'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change PIN Modal */}
      <Dialog open={isChangePinOpen} onOpenChange={setIsChangePinOpen}>
        <DialogContent className="bg-surface max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary">Change Login PIN</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleChangePin} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="current-pin">Current PIN *</Label>
              <Input
                id="current-pin"
                type="password"
                value={pinChangeData.currentPin}
                onChange={(e) => setPinChangeData({ ...pinChangeData, currentPin: e.target.value })}
                placeholder="Enter current 5-digit PIN"
                maxLength={5}
                pattern="[0-9]{5}"
                className="h-12 mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="new-pin">New PIN *</Label>
              <Input
                id="new-pin"
                type="password"
                value={pinChangeData.newPin}
                onChange={(e) => setPinChangeData({ ...pinChangeData, newPin: e.target.value })}
                placeholder="Enter new 5-digit PIN"
                maxLength={5}
                pattern="[0-9]{5}"
                className="h-12 mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="confirm-new-pin">Confirm New PIN *</Label>
              <Input
                id="confirm-new-pin"
                type="password"
                value={pinChangeData.confirmNewPin}
                onChange={(e) => setPinChangeData({ ...pinChangeData, confirmNewPin: e.target.value })}
                placeholder="Re-enter new PIN"
                maxLength={5}
                pattern="[0-9]{5}"
                className="h-12 mt-1"
                required
              />
            </div>

            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">
                ⚠️ Important: After changing your PIN, you'll need to use the new PIN for your next login.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsChangePinOpen(false);
                  setPinChangeData({ currentPin: '', newPin: '', confirmNewPin: '' });
                }}
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 bg-accent text-white hover:bg-accent/90"
              >
                Change PIN
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Staff Management PIN Verification Modal */}
      <Dialog open={staffPinOpen} onOpenChange={setStaffPinOpen}>
        <DialogContent className="bg-surface max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <Key className="h-5 w-5 text-purple-600" />
              Enter PIN to Access Staff Management
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const franchisePin = user?.franchise?.pin_code?.toString() || '';
            if (staffPin === franchisePin) {
              setStaffPinOpen(false);
              setStaffPin('');
              setIsStaffManagementOpen(true);
              toast.success('Staff Management unlocked');
              // Check if there's a pending tour
              if (pendingStaffTour) {
                setPendingStaffTour(false);
                // Tour will be shown inside the StaffManagementV2 component
              }
            } else {
              toast.error('Invalid PIN');
              setStaffPin('');
            }
          }} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="staff-pin">Franchise PIN</Label>
              <Input
                id="staff-pin"
                type="password"
                value={staffPin}
                onChange={(e) => setStaffPin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="Enter your 5-digit PIN"
                maxLength={5}
                pattern="[0-9]{5}"
                className="h-12 mt-1 text-center text-2xl tracking-widest"
                autoFocus
                required
              />
            </div>
            
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-xs text-purple-800">
                🔒 Staff Management contains sensitive employee data. Enter your franchise PIN to access.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setStaffPinOpen(false);
                  setStaffPin('');
                  setPendingStaffTour(false);
                }}
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 bg-purple-600 text-white hover:bg-purple-700"
                disabled={staffPin.length !== 5}
              >
                Unlock
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Staff Management Dialog */}
      <Dialog open={isStaffManagementOpen} onOpenChange={setIsStaffManagementOpen}>
        <DialogContent className="bg-surface max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Staff Management
            </DialogTitle>
          </DialogHeader>
          <StaffManagementV2 franchiseId={user.franchise.id} />
        </DialogContent>
      </Dialog>

      {/* Attendance Modal */}
      <AttendancePunch
        franchiseId={user.franchise.id}
        isOpen={isAttendanceOpen}
        onClose={() => setIsAttendanceOpen(false)}
      />

      {/* Reports PIN Verification Modal */}
      <Dialog open={isReportsPinOpen} onOpenChange={setIsReportsPinOpen}>
        <DialogContent className="bg-surface max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Enter PIN to View Reports
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            // Verify PIN - pin_code is the field name in franchise data
            const franchisePin = user?.franchise?.pin_code?.toString() || '';
            if (reportsPin === franchisePin) {
              setIsReportsUnlocked(true);
              setActiveTab('reports');
              setIsReportsPinOpen(false);
              setReportsPin('');
              toast.success('Reports unlocked');
              // Check if there's a pending tour
              if (pendingReportsTour) {
                setPendingReportsTour(false);
                setTimeout(() => {
                  setForceTourRun(true);
                  setCurrentTourId('reports');
                }, 500);
              }
            } else {
              toast.error('Invalid PIN');
              setReportsPin('');
            }
          }} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="reports-pin">Franchise PIN</Label>
              <Input
                id="reports-pin"
                type="password"
                value={reportsPin}
                onChange={(e) => setReportsPin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="Enter your 5-digit PIN"
                maxLength={5}
                pattern="[0-9]{5}"
                className="h-12 mt-1 text-center text-2xl tracking-widest"
                autoFocus
                required
              />
            </div>
            
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                🔒 Reports contain sensitive sales data. Enter your franchise PIN to access.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsReportsPinOpen(false);
                  setReportsPin('');
                  setPendingReportsTour(false);
                }}
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 bg-primary text-white hover:bg-primary/90"
                disabled={reportsPin.length !== 5}
              >
                Unlock Reports
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Expense Tracker Modal */}
      {showExpenseTracker && (
        <div className="fixed inset-0 bg-background z-50 overflow-auto">
          <FranchiseExpenseTracker
            franchiseId={user?.franchise?.id}
            franchiseName={user?.franchise?.name}
            onBack={() => setShowExpenseTracker(false)}
            onAttendanceClick={() => {
              setShowExpenseTracker(false);
              setIsAttendanceOpen(true);
            }}
          />
        </div>
      )}

      {/* Start Tour Dialog */}
      <StartTourDialog 
        open={showTourDialog} 
        onOpenChange={setShowTourDialog}
        onStartTour={handleStartTour}
      />

      {/* FOS Watermark */}
      <FOSWatermark opacity={0.06} size="md" />

    </div>
  );
};

export default FranchiseDashboard;